import { useCallback, useState, useMemo } from "react";
import { Check, ChevronsUpDown, Search, X } from "lucide-react";

import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";

import { cn } from "@/lib/utils";
import type { SelectSearchableOption } from "./form-select-searchable";

export type SelectSearchableProps = {
  value?: string | number | null;
  onChange?: (value: string | number | null) => void;
  /** Called whenever the search input changes — useful for driving server-side search. */
  onSearchChange?: (search: string) => void;
  options: SelectSearchableOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  label?: string;
  disabled?: boolean;
  allowClear?: boolean;
  triggerClassName?: string;
  contentClassName?: string;
  wrapperClassName?: string;
};

/**
 * Standalone searchable select — same UI as FormSelectSearchable but
 * works outside a react-hook-form context (no `control` / `name` required).
 */
export const SelectSearchable = ({
  value,
  onChange,
  onSearchChange,
  options,
  placeholder = "Select option...",
  searchPlaceholder = "Search...",
  label,
  disabled,
  allowClear = true,
  triggerClassName,
  contentClassName,
  wrapperClassName
}: SelectSearchableProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const handleSearchChange = (val: string) => {
    setSearch(val);
    onSearchChange?.(val);
  };

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(search.toLowerCase())
    );
  }, [options, search]);

  const getOptionLabel = useCallback(
    (val: string | number | null | undefined) => {
      if (val === null || val === undefined || val === "") return null;
      return options.find(
        (option) => option.value.toString() === val.toString()
      )?.label;
    },
    [options]
  );

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange?.(null);
    handleSearchChange("");
  };

  return (
    <div className={cn("aero:flex aero:flex-col aero:gap-1", wrapperClassName)}>
      {label && (
        <span className="aero:text-sm aero:font-medium aero:text-neutral-700">
          {label}
        </span>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            role="combobox"
            disabled={disabled}
            aria-expanded={open}
            className={cn(
              "aero:flex aero:h-10 aero:w-full aero:items-center aero:justify-between",
              "aero:px-3 aero:py-2 aero:gap-2 aero:cursor-pointer",
              "aero:bg-white aero:border aero:border-neutral-300 aero:rounded",
              "aero:font-inter aero:font-normal aero:text-sm aero:leading-5",
              "aero:focus-visible:outline-none aero:focus-visible:border-ring aero:focus-visible:ring-ring/50 aero:focus-visible:ring-[3px]",
              "aero:data-[state=open]:border-ring aero:data-[state=open]:ring-ring/50 aero:data-[state=open]:ring-[3px]",
              disabled && "aero:opacity-50 aero:cursor-not-allowed",
              triggerClassName
            )}
          >
            <span
              className={cn(
                "aero:truncate",
                value ? "aero:text-foreground" : "aero:text-neutral-500"
              )}
            >
              {getOptionLabel(value) || placeholder}
            </span>
            <div className="aero:flex aero:items-center aero:gap-1 aero:flex-shrink-0">
              {allowClear && value != null && value !== "" && (
                <span
                  role="button"
                  className="aero:p-0.5 aero:rounded-full aero:hover:bg-neutral-100"
                  onClick={handleClear}
                >
                  <X className="aero:h-3 aero:w-3 aero:text-neutral-500" />
                </span>
              )}
              <ChevronsUpDown className="aero:h-5 aero:w-5 aero:text-neutral-500" />
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent
          className={cn(
            "aero:w-(--radix-popover-trigger-width) aero:p-0",
            contentClassName
          )}
          align="start"
        >
          <div className="aero:flex aero:items-center aero:border-b aero:px-3">
            <Search className="aero:h-4 aero:w-4 aero:shrink-0 aero:opacity-50" />
            <Input
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="aero:h-9 aero:border-0 aero:shadow-none aero:focus-visible:ring-0"
            />
            {search && (
              <span
                role="button"
                className="aero:p-1 aero:rounded-full aero:hover:bg-muted aero:cursor-pointer"
                onClick={() => handleSearchChange("")}
              >
                <X className="aero:h-3 aero:w-3 aero:opacity-50" />
              </span>
            )}
          </div>
          <ScrollArea className="aero:h-60">
            {filteredOptions.length === 0 ? (
              <div className="aero:m-2 aero:flex aero:items-center aero:justify-center aero:rounded aero:border aero:border-neutral-300 aero:bg-neutral-100 aero:py-4 aero:px-2 aero:h-56">
                <span className="aero:font-inter aero:font-normal aero:text-sm aero:text-center aero:text-foreground">
                  No results found
                </span>
              </div>
            ) : (
              <div className="aero:p-1">
                {filteredOptions.map((option) => (
                  <div
                    key={option.value}
                    role="option"
                    aria-selected={
                      value?.toString() === option.value.toString()
                    }
                    className={cn(
                      "aero:relative aero:flex aero:cursor-pointer aero:select-none aero:items-center aero:rounded-sm aero:px-2 aero:py-1.5 aero:text-sm aero:outline-none",
                      "aero:hover:bg-neutral-100 aero:hover:text-foreground",
                      value?.toString() === option.value.toString() &&
                        "aero:bg-brand-primary-50 aero:text-brand-primary-600"
                    )}
                    onClick={() => {
                      onChange?.(option.value);
                      setOpen(false);
                      handleSearchChange("");
                    }}
                  >
                    <span className="aero:truncate">{option.label}</span>
                    {value?.toString() === option.value.toString() && (
                      <Check className="aero:ml-auto aero:h-4 aero:w-4" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </PopoverContent>
      </Popover>
    </div>
  );
};
